import { useEffect } from 'react';

/**
 * الاختصارات بالأكواد الفيزيائية (§8 و§6.4): `event.code` لا `event.key` —
 * فمفتاح C في التخطيط العربي يُنتج «ذ»، و`code` يبقى `KeyC` في التخطيطين.
 * هذا قيد إتاحة وصول ملزم، لا تحسينًا.
 */
export interface Shortcut {
  /** مثل 'KeyK' أو 'KeyC' أو 'Slash' — كود فيزيائي دائمًا. */
  readonly code: string;
  /** يشترط Ctrl على ويندوز/لينكس أو ⌘ على macOS. */
  readonly withCmdOrCtrl?: boolean;
  readonly withShift?: boolean;
  readonly handler: () => void;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'textbox'
  );
}

export function useShortcuts(shortcuts: readonly Shortcut[]): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.repeat) return;
      const mod = event.metaKey || event.ctrlKey;

      for (const shortcut of shortcuts) {
        if (event.code !== shortcut.code) continue;
        if ((shortcut.withCmdOrCtrl ?? false) !== mod) continue;
        if ((shortcut.withShift ?? false) !== event.shiftKey) continue;
        // الاختصار المجرد (بلا مُعدِّل) لا يسرق حرفًا من حقل كتابة؛
        // اختصارات ⌘/Ctrl تعمل أثناء الكتابة لأنها لا تُنتج حرفًا.
        if (!mod && isEditableTarget(event.target)) continue;
        if (event.altKey) continue;

        event.preventDefault();
        shortcut.handler();
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcuts]);
}
